import { ExpandMore } from "@mui/icons-material";
import { Chip, Grid2, IconButton, useTheme } from "@mui/material";
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DetailedServiceBookingData } from "../Domain/RemoteOrderRequests.types";
import useServiceTypeColor from "../hooks/useServiceTypeColor";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_Row,
  MRT_RowSelectionState,
  useMaterialReactTable
} from "material-react-table";
import { OrderRequestRoomLayoutRow } from "../Domain/OrderRequestList.types";
import {
  renderServiceImage,
  renderServiceSetupTime
} from "../../../FacilityManager/Components/Toolbars/InfoSidebar/ZoneInfoSidebar/ServicePartial/ServiceCommonRender.partial";
import { commonLocalization } from "../../../../functions/tableLocalization";
import { renderRoomLayoutCell } from "../../../FacilityManager/Components/Toolbars/InfoSidebar/ZoneInfoSidebar/RoomLayoutDialogContent/RoomLayoutTable.partial";
import { handleInitialItemSelect } from "../functions/handleInitialItemSelect.function";
import { DetailDataChanges } from "./OrderRequestDetailDialog.partial";
import { BookingRequestsStatus } from "../../../BookingRequest/typings/booking-requests.types";
import {
  ServiceDeliveryStatus,
  ServiceType
} from "../../../../features/Booking-Form/typings/booking-service-administration.types";
import { statusEditSelectOpts } from "./OrderRequestCardItem.partial";
import { renderServiceLineStatusLabelCell } from "./OrderRequestDetailServiceLineStatusCell.partial";
import { EnlargedImageDialog } from "../../../../features/EnlargedImageCarousel/enlarged-image-dialog.component";
import { RemoteRoomlayout } from "../../../FacilityManager/Domain/Types/FloorPlan/RoomlayoutAssignment.type";
import { withSeeditMrtInitialState } from "../../../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../../../themes/use-seedit-mrt-table-options";

type Props = {
  listOpen: boolean;
  setListOpen: (value: SetStateAction<{ [key: number]: boolean }>) => void;
  detailData: DetailedServiceBookingData;
  editedData: DetailedServiceBookingData | null;
  handleDetailData: (changes: DetailDataChanges) => void;
  roomlayoutRowSelection: MRT_RowSelectionState;
  setRoomlayoutRowSelection: Dispatch<SetStateAction<MRT_RowSelectionState>>;
  remoteRoomLayout: RemoteRoomlayout[] | undefined;
  isArchive: boolean;
  isBookingCancelled: boolean;
  isPostLoading: boolean;
  chipStyle: any;
  /** Service admin can edit when order progress is READY (same as catering available qty). */
  isServiceAdminBypass?: boolean;
};

export function OrderRequestDetailRoomLayoutTable({
  listOpen,
  setListOpen,
  detailData,
  editedData,
  handleDetailData,
  roomlayoutRowSelection,
  setRoomlayoutRowSelection,
  remoteRoomLayout,
  isArchive,
  isBookingCancelled,
  isPostLoading,
  chipStyle,
  isServiceAdminBypass = false
}: Readonly<Props>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { layoutColor } = useServiceTypeColor();

  const [roomlayoutData, setRoomlayoutData] = useState<OrderRequestRoomLayoutRow[]>([]);
  const [showBiggerImage, setShowBiggerImage] = useState<string | null>(null);

  const handleChangeStatus = (
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    row: MRT_Row<OrderRequestRoomLayoutRow>
  ) => {
    handleDetailData({
      type: ServiceType.Room_Layout,
      id: row.original.id,
      originalQty: row.original.quantity,
      newQty: row.original.quantity,
      status: event.target.value as BookingRequestsStatus
    });
  };

  useEffect(() => {
    if (!editedData || !remoteRoomLayout) return;

    const modified = editedData.roomLayouts.map(el => {
      const found = remoteRoomLayout.find(layout => layout.id === el.id);
      return {
        ...el,
        name: found?.name,
        layout: found?.arrangement ?? [],
        picturePaths: found?.picturePaths
      };
    });

    setRoomlayoutData(modified);
    handleInitialItemSelect(
      isArchive,
      isBookingCancelled,
      editedData.roomLayouts,
      setRoomlayoutRowSelection
    );
  }, [isArchive, isBookingCancelled, editedData?.roomLayouts, remoteRoomLayout]);

  const column = useMemo<MRT_ColumnDef<OrderRequestRoomLayoutRow>[]>(
    () => [
      {
        accessorKey: "layout",
        header: t("Layout"),
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => renderRoomLayoutCell(cell.row.original.layout, theme),
        size: 160,
        enableEditing: false
      },
      {
        accessorKey: "status",
        header: t("Status"),
        Cell: ({ cell }) => renderServiceLineStatusLabelCell(cell.row.original.status, t),
        editVariant: "select",
        size: 100,
        editSelectOptions: () => statusEditSelectOpts(),
        muiEditTextFieldProps: ({ row }) => {
          return {
            onChange: event => handleChangeStatus(event, row)
          };
        },
        enableEditing: row => row.original.status !== BookingRequestsStatus.CANCELLED
      },
      {
        accessorKey: "name",
        header: t("Name"),
        size: 200,
        enableEditing: false
      },
      {
        header: t("Setup Time"),
        accessorKey: "setupTime",
        size: 150,
        enableEditing: false,
        Header: renderServiceSetupTime()
      },
      {
        accessorKey: "picturePaths",
        header: t("Image"),
        enableEditing: false,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) =>
          renderServiceImage(
            100,
            cell.row.original.picturePaths
              ? cell.row.original.picturePaths[cell.row.original.picturePaths.length - 1]
              : null,
            setShowBiggerImage
          ),
        size: 100
      }
    ],
    [roomlayoutData, t]
  );

  const deliveryLockedForEditing =
    editedData?.deliveryStatus === ServiceDeliveryStatus.READY && !isServiceAdminBypass;
  const seeditMrtOptions = useSeeditMrtTableOptions<OrderRequestRoomLayoutRow>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    state: {
      rowSelection: roomlayoutRowSelection,
      isSaving: isPostLoading
    },
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 10, pageIndex: 0 }
    }),
    muiPaginationProps: { rowsPerPageOptions: [5, 10, 15, 20, 30], variant: "outlined" },
    localization: {
      ...commonLocalization(t),
      noRecordsToDisplay: t("There are no room layout")
    },
    getRowId: row => row.id?.toString(),
    onRowSelectionChange: setRoomlayoutRowSelection,
    positionToolbarAlertBanner: "none",
    paginationDisplayMode: "pages",
    enableDensityToggle: false,
    autoResetPageIndex: false,
    enableFullScreenToggle: false,
    enableHiding: true,
    enableRowSelection: false,
    editDisplayMode: "table",
    enableEditing: !isArchive && !isBookingCancelled && !deliveryLockedForEditing,
    columns: column,
    data: roomlayoutData // editedData?.roomLayouts
  });

  return (
    <Grid2 container sx={{ width: "100%", flexDirection: "column" }}>
      <Grid2>
        {/* header of room layout table */}
        <IconButton
          data-testid={"expandButton"}
          sx={[
            {
              transition: theme.transitions.create("transform", {
                duration: theme.transitions.duration.shortest
              })
            },
            listOpen ? { transform: "rotate(0deg)" } : { transform: "rotate(-90deg)" }
          ]}
          onClick={() => setListOpen(prev => ({ ...prev, [2]: !prev[2] }))}
        >
          <ExpandMore />
        </IconButton>

        <Chip
          label={t("OrderRequest_Layout", { number: detailData.numberOfRoomLayouts })}
          sx={{ ...chipStyle, backgroundColor: layoutColor }}
        />
      </Grid2>

      {/* bookable equipments table */}
      <Grid2 sx={{ pl: 5 }}>
        <MaterialReactTable table={table} />
      </Grid2>

      <EnlargedImageDialog
        open={!!showBiggerImage}
        picturePath={showBiggerImage}
        handleClose={() => setShowBiggerImage(null)}
      />
    </Grid2>
  );
}
